/**
 * Validity stamp for a resumable run held in localStorage.
 *
 * Restored state is only useful if it still describes the lesson in front of
 * the trainee. Two rounds of review found the same shape of bug three times —
 * a finished run restored after an admin reissued the module, a passed verdict
 * restored after a reset, answers restored against a lesson whose questions had
 * since moved — so the fix is one concept rather than three patches: nothing
 * comes back out of localStorage unless it was written by THIS account against
 * THIS content.
 *
 * `results` is indexed by the global question index from
 * `flattenQuestions(pages)`. Insert a question on page 1 and every later index
 * shifts, so a resumed run would show attempt counts and correct/incorrect
 * styling against questions the trainee never answered — and a page could read
 * as already settled, letting them past without answering. The fingerprint
 * makes an edited lesson start clean instead of scrambled.
 *
 * Server-side invalidation (reset, reissue) is a separate signal and lives with
 * the progress document — see `isReissuePending` in lessonScoring.
 */

/**
 * Bump when the stored shape changes, to discard everything older.
 *
 * 2 adds a timestamp, so a run can be compared against an admin resetting the
 * module. Bumping it discards every run written before that — which is the
 * point: those runs cannot be compared against anything, and one of them was
 * the stale run this change exists to clear.
 */
export const RUN_VERSION = 2

/** FNV-1a, as base36. Not security — just a short, stable content id. */
function hash(text) {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

/**
 * A fingerprint of everything about a lesson that a stored run depends on: each
 * question's text, option count and correct answer, and where the page
 * boundaries fall between them.
 *
 * Page titles are deliberately excluded — fixing a typo in a heading shifts no
 * indices and shouldn't throw away someone's half-finished run.
 */
export function contentFingerprint(pages = []) {
  const parts = []
  for (const page of pages) {
    // A page marker, not a count: the questions that follow already say how
    // many there are. What the marker adds is WHERE the page boundaries fall,
    // which is what decides whether a page reads as settled. Without it,
    // [[q1], [q2]] and [[q1, q2]] would fingerprint identically. It cannot
    // collide with a question entry, because those always contain a pipe.
    parts.push('PAGE')
    for (const q of page?.questions || []) {
      parts.push(`${q?.question || ''}|${(q?.options || []).length}|${q?.correctIndex}`)
    }
  }
  return hash(parts.join('~'))
}

/**
 * The only fields a stored run may carry: where they are up to, what they have
 * answered, and whether they are part-way through a retake.
 *
 * This is an allowlist rather than a convention because the fields NOT on it
 * are the ones that caused the trouble. Runs used to store `finished` and
 * `summary`, and that local claim of completion was what drew the completion
 * screen — so a run whose write never reached Firestore, or which was read
 * before the signed-in user was known, could show a trainee a completion that
 * did not exist. Completion is the server's answer now
 * (`docs/completion-truth.md`), and filtering here means a run packed by the
 * OLD code still resumes its answers while its verdict is simply unreadable.
 *
 * `retaking` is on the list and `finished` is not, and the difference is the
 * test for anything added here: **can this field make the app claim a module is
 * complete?** `retaking` cannot. It only ever sends the trainee INTO the lesson,
 * so its worst failure is reopening a module somebody had finished. Leaving it
 * out was worse: a retake lived only in page-session state, so reloading
 * mid-retake dropped the trainee back on their old result screen, where the only
 * way into the lesson deleted the answers they had just given.
 *
 * Widening this list with anything that CAN claim completion is how the second
 * source of truth comes back.
 */
const RESUMABLE = ['pageIndex', 'results', 'retaking']

/** Just the resumable fields, so neither end can smuggle a verdict through. */
function pick(state) {
  const out = {}
  for (const key of RESUMABLE) {
    if (state?.[key] !== undefined) out[key] = state[key]
  }
  return out
}

/**
 * Serialise run state with the stamp that makes it verifiable later.
 *
 * `at` is when this was written, in milliseconds. It exists so the run can be
 * compared against a reset: an admin wiping the module leaves a `resetAt` on
 * the progress document, and a local run older than that is finished with.
 * Passed in rather than read from the clock here so the function stays pure.
 *
 * Only `RESUMABLE` fields are written. See the allowlist above for why that is
 * enforced here rather than left to each caller to remember.
 */
export function packRun(state, { uid, fingerprint, at = Date.now() }) {
  return JSON.stringify({
    __v: RUN_VERSION, __uid: uid || null, __fp: fingerprint, __t: at,
    ...pick(state),
  })
}

/**
 * Read run state back, or null if it cannot be trusted: wrong version, a
 * different account, changed content, or unparseable. Null means "start
 * fresh", which is always safe — Firestore holds the authoritative record of
 * what has been completed.
 */
export function unpackRun(raw, { uid, fingerprint }) {
  if (!raw) return null

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  // Unstamped state predates this check and cannot be verified, so it goes.
  if (parsed.__v !== RUN_VERSION) return null
  if ((parsed.__uid || null) !== (uid || null)) return null
  if (parsed.__fp !== fingerprint) return null

  const state = pick(parsed)
  // When this run was last written, so a caller can tell whether an admin has
  // wiped the module since. 0 rather than undefined: an unknown age is older
  // than any reset, which fails towards starting clean.
  state.savedAt = typeof parsed.__t === 'number' ? parsed.__t : 0
  return state
}
